import { useTranslation } from 'react-i18next'
import { UserCheck, Home, CalendarClock } from 'lucide-react'
import { Badge, CardKebabMenu } from '@/components/ui'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { SharedOwnerLease } from '@/types'

export interface SharedOwnerCardProps {
  sharedOwner: SharedOwnerLease
  onEdit: (sharedOwner: SharedOwnerLease) => void
  onEnd: (sharedOwner: SharedOwnerLease) => void
}

/**
 * Co-owner list card (EP-111 T111-9, D11). Mirrors CustomerCard's layout —
 * avatar + name on the left, a usage-status Badge top-right, contact/context
 * rows below — but drops the line/unpaid-invoice chips (not relevant to
 * co-owners) and swaps CustomerCard's `<Link to detail>` for a `CardKebabMenu`
 * (Edit / End): there is no co-owner detail page yet (the backend exposes
 * only GET index + POST create + PATCH update, no GET show), so Edit opens a
 * modal pre-filled from this already-fetched list row instead of navigating
 * to a route that would need a fetch-by-id endpoint that doesn't exist.
 * Renamed from CoOwnerCard by EP-133 SS4b-2 (2026-08-20).
 */
export function SharedOwnerCard({ sharedOwner, onEdit, onEnd }: SharedOwnerCardProps) {
  const { t } = useTranslation('shared_owners')
  const formatDate = useDateFormatter()

  // D15 — effective_usage_status is the value to DISPLAY (it overrides to
  // 'rented' when an active tenant RentalLease exists on the unit); the raw
  // usage_status never itself carries 'rented'.
  const usageStatus = sharedOwner.effective_usage_status ?? sharedOwner.usage_status

  const statusBadge = (() => {
    switch (usageStatus) {
      case 'owner_occupied':
        return <Badge variant="ghost">{t('status.owner_occupied')}</Badge>
      case 'available_for_rent':
        return <Badge variant="warning">{t('status.available_for_rent')}</Badge>
      case 'rented':
        return <Badge variant="success">{t('status.rented')}</Badge>
      case 'for_sale':
        return <Badge variant="info">{t('status.for_sale')}</Badge>
      default:
        return null
    }
  })()

  const alreadyEnded = sharedOwner.status === 'ended' || !!sharedOwner.ended_at
  const property = sharedOwner.unit.room_type.property

  return (
    <div
      className="p-4 bg-base-100 border border-base-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
      data-testid={`co-owner-card-${sharedOwner.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <UserCheck className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold truncate">{sharedOwner.customer.name}</h3>
          </div>
        </div>
        {/* Status badge + kebab menu sit top-right of the card (mirrors
            CustomerCard's status-badge placement). */}
        <div className="flex items-center gap-1.5 shrink-0">
          {statusBadge}
          <CardKebabMenu
            items={[
              { label: t('actions.edit'), onClick: () => onEdit(sharedOwner) },
              ...(alreadyEnded
                ? []
                : [{ label: t('actions.end'), variant: 'error' as const, onClick: () => onEnd(sharedOwner) }]),
            ]}
          />
        </div>
      </div>
      <div className="space-y-1 text-sm text-base-content/60">
        <div className="flex items-center gap-2">
          <Home className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {property.name} · {sharedOwner.unit.number}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <CalendarClock className="w-3 h-3 shrink-0" />
          <span>
            {t('labels.started_at')} {formatDate(sharedOwner.started_at)}
          </span>
        </div>
      </div>
    </div>
  )
}
